import React, { useEffect, useState } from 'react';
import './Toast.css';

export type ToastType = 'success' | 'info' | 'warning' | 'error' | 'game' | 'connection';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
  icon?: string;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 3500, // Default 3.5s (reduced from 4s)
  onClose,
  icon
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Slide in animation - faster for snappier feel
    const showTimer = setTimeout(() => setIsVisible(true), 30);
    
    // Auto dismiss with pause support
    let hideTimer: NodeJS.Timeout;
    
    if (!isPaused) {
      hideTimer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onClose(id), 200); // Faster exit (reduced from 300ms)
      }, duration);
    }

    return () => {
      clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [id, duration, onClose, isPaused]);

  const getToastStyle = () => {
    switch (type) {
      case 'success':
        return {
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.25), rgba(22, 163, 74, 0.15))',
          border: '2px solid rgba(34, 197, 94, 0.4)',
          boxShadow: '0 8px 32px rgba(34, 197, 94, 0.25)',
          color: '#22c55e'
        };
      case 'error':
        return {
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.15))',
          border: '2px solid rgba(239, 68, 68, 0.4)',
          boxShadow: '0 8px 32px rgba(239, 68, 68, 0.25)',
          color: '#ef4444'
        };
      case 'warning':
        return {
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.25), rgba(217, 119, 6, 0.15))',
          border: '2px solid rgba(245, 158, 11, 0.4)',
          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.25)',
          color: '#f59e0b'
        };
      case 'game':
        return {
          background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.25), rgba(67, 56, 202, 0.15))',
          border: '2px solid rgba(139, 92, 246, 0.4)',
          boxShadow: '0 8px 32px rgba(79, 70, 229, 0.25)',
          color: '#a78bfa'
        };
      case 'connection':
        return {
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.25), rgba(14, 165, 233, 0.15))',
          border: '2px solid rgba(6, 182, 212, 0.4)',
          boxShadow: '0 8px 32px rgba(6, 182, 212, 0.25)',
          color: '#06b6d4'
        };
      default: // info
        return {
          background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.25), rgba(71, 85, 105, 0.15))',
          border: '2px solid rgba(148, 163, 184, 0.4)',
          boxShadow: '0 8px 32px rgba(100, 116, 139, 0.25)',
          color: '#cbd5e1'
        };
    }
  };

  const getDefaultIcon = () => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'game': return '🎮';
      case 'connection': return '🔗';
      default: return 'ℹ️';
    }
  };

  return (
    <div 
      className={`toast toast-${type} ${isVisible ? 'toast-visible' : ''} ${isExiting ? 'toast-exit' : ''}`}
      style={getToastStyle()}
      onClick={() => onClose(id)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      tabIndex={0}
    >
      {/* Decorative backdrop effect */}
      <div className="toast-backdrop" aria-hidden="true" />
      
      <div className="toast-content">
        <div className="toast-icon">
          {icon || getDefaultIcon()}
        </div>
        
        <div className="toast-text">
          <div className="toast-title">{title}</div>
          <div className="toast-message">{message}</div>
        </div>
        
        <button 
          className="toast-close"
          onClick={(e) => {
            e.stopPropagation();
            setIsExiting(true);
            setTimeout(() => onClose(id), 200); // Faster exit
          }}
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
      
      {/* Progress bar */}
      <div 
        className="toast-progress"
        style={{ 
          animationDuration: `${duration}ms`,
          background: type === 'success' ? '#22c55e' : 
                     type === 'error' ? '#ef4444' :
                     type === 'warning' ? '#f59e0b' :
                     type === 'game' ? '#a78bfa' :
                     type === 'connection' ? '#06b6d4' : '#cbd5e1'
        }}
      />
    </div>
  );
};

export default Toast;